import { Box, Chip, styled, TableCell, TableRow } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import React from "react";
import { useTranslation } from "react-i18next";

import { Amortization, Mortgage, OtherMortgage } from "../../../../../model/MortgageExtensionModel";
import { useFinanceCalculatorService } from "../../../../../services/finance-calculator/FinanceCalculatorServiceContext";
import { addYears, DateFormat, formatDate } from "../../../../../utils/DateUtils";
import { formatAmount } from "../../../../../utils/NumberUtils";
import { renderProductType } from "../../../../../utils/Utils";
import {
    MortgageSummaryBold,
    MortgageSummaryLabel,
    MortgageSummaryLabelSmall,
} from "../../../2-summary/NewMortgageSummary";

const Cell = styled(TableCell)(({ theme }) => ({
    border: "none",
    padding: "8px 15px",
}));

const FirstRowCell = styled(Cell)(({ theme }) => ({
    padding: "16px 15px 0 15px",
}));

const TableChip = styled(Chip)(({ theme }) => ({
    borderRadius: 2,
    lineHeight: "1.1",
}));

const MortgageRowDesktop = (props: { mortgage: Mortgage | OtherMortgage; isNew: boolean }) => {
    const { mortgage, isNew } = props;
    const { i18n, t } = useTranslation();
    const financeCalculatorService = useFinanceCalculatorService();

    const backgroundColor = isNew ? "#0093D1" : "#B4D7FA";
    const textColor = isNew ? "#fff" : "rgba(0, 0, 0, 0.54)";
    const label = isNew ? t("General.New") : t("General.Existing");

    function amortizationLabel(amortization: Amortization): string {
        switch (amortization.period) {
            case "year":
                return t("ConfigurationScreen.Annually");
            case "sem":
                return t("ConfigurationScreen.Every six months");
            case "quar":
                return t("ConfigurationScreen.Quarterly");
            case "mth":
                return t("ConfigurationScreen.End of month");
            default:
                return "";
        }
    }

    function isLastAmortization(index: number) {
        return index === mortgage.amortizations.length - 1;
    }

    function monthlyPayment(): number {
        if (isNew) {
            return (mortgage as Mortgage).monthlyPayment;
        }
        return mortgage.interestRate > 0
            ? financeCalculatorService.calcMonthlyAmount(mortgage.amount, mortgage.interestRate)
            : 0;
    }

    function printExpirationDate() {
        if (isNew) {
            return addYears(mortgage.expirationDate, +mortgage.duration);
        }
        return mortgage.expirationDate !== null && "hypothek_var" !== mortgage.productType
            ? formatDate(mortgage.expirationDate, DateFormat.DATE)
            : null;
    }

    return (
        <TableBody sx={{ borderLeft: `4px solid ${backgroundColor}`, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            <TableRow sx={{ flexGrow: 1 }} key={"badge" + mortgage.expirationDate}>
                <FirstRowCell>
                    <TableChip
                        label={label}
                        size={"small"}
                        sx={{ backgroundColor: backgroundColor, color: textColor }}
                    />
                </FirstRowCell>
                <FirstRowCell colSpan={4} />
            </TableRow>
            <TableRow sx={{ flexGrow: 1, height: "52px" }} key={"details" + mortgage.expirationDate}>
                <Cell sx={{ paddingTop: 0 }}>
                    <MortgageSummaryBold>{renderProductType(mortgage, i18n, t)}</MortgageSummaryBold>
                </Cell>
                <Cell sx={{ paddingTop: 0 }}>
                    <Box display={"flex"} flexDirection={"column"} flex={1}>
                        <MortgageSummaryLabel>
                            {mortgage.duration} {t("General.Years")}
                        </MortgageSummaryLabel>
                        <MortgageSummaryLabelSmall>{printExpirationDate()}</MortgageSummaryLabelSmall>
                    </Box>
                </Cell>
                <Cell align="right" sx={{ paddingTop: 0 }}>
                    <MortgageSummaryLabel> {formatAmount(mortgage.amount)} </MortgageSummaryLabel>
                </Cell>
                <Cell align="right" sx={{ paddingTop: 0 }}>
                    <MortgageSummaryLabel>{formatAmount(mortgage.interestRate)} %</MortgageSummaryLabel>
                </Cell>
                <Cell align="right" sx={{ paddingTop: 0 }}>
                    <MortgageSummaryLabel>{formatAmount(monthlyPayment())}</MortgageSummaryLabel>
                </Cell>
            </TableRow>
            {mortgage.amortizations &&
                mortgage.amortizations.map((amortization, index) => (
                    <TableRow sx={{ flexGrow: 1 }} key={"amortization" + mortgage.expirationDate + index}>
                        <Cell sx={{ paddingBottom: isLastAmortization(index) ? "16px" : "8px" }}>
                            <MortgageSummaryLabel>{amortization.type}</MortgageSummaryLabel>
                            <MortgageSummaryLabelSmall>
                                {amortizationLabel(amortization)}: {formatAmount(amortization.amount)}
                            </MortgageSummaryLabelSmall>
                        </Cell>
                        <Cell colSpan={3} />
                        <Cell align="right">
                            <MortgageSummaryLabel>{formatAmount(amortization.monthlyAmount)}</MortgageSummaryLabel>
                        </Cell>
                    </TableRow>
                ))}
        </TableBody>
    );
};
export default MortgageRowDesktop;
